/**
 * PRP-INTAKE-2 (work item 3): the project Intake tab.
 *
 * Reads GET /mcp/projects/{id}/intake: every requirement that applies to the claim, filled or
 * open, as stages in the order the server returns (the loss type decides top-down or
 * bottom-up), groups within each stage (Claim, an elevation, a room). Each requirement shows its
 * question, current value, where the value came from (a source badge, "Open" while unanswered)
 * and a control to answer or change it per `input.type`. Each stage saves its pending changes
 * in one POST /mcp/projects/{id}/interview/answer (the chat's answer_open_items semantics), then
 * refetches, so the page and the chat share one record. "Continue in chat" copies a prompt that
 * tells the chat to pick up the open items. Same order at 390 px as at a desk (spec 9, ruling 5).
 * Text in requirements is data, shown as text.
 *
 * PRP-WEB-1 W2.7: under the jurisdiction, "Answered in chat" (ChatAnswersSection) lists what the
 * chat asked and the user answered there, and what is still waiting in the chat.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { IntakeAPI } from '../../lib/intakeApi';
import type { IntakeAnswer, IntakeRequirement, ProjectIntake } from '../../lib/intakeApi';
import ChatAnswersSection from './ChatAnswersSection';
import { IntakeGroupBlock, answerOf, initialDraft, sameDraft } from './IntakeGroupBlock';
import type { Draft } from './IntakeGroupBlock';
import { Banner } from './ui/banner';
import { Button } from './ui/button';

/** Props for {@link IntakeTab}. */
export interface IntakeTabProps {
  projectId: string;
  /** Fallback name for the "Continue in chat" prompt when the intake has no project_name. */
  projectName: string;
}


export function chatPrompt(projectName: string): string {
  return `Continue the intake interview for project ${projectName} in AIEA: call get_open_items and ask me what is still open.`;
}

function messageOf(err: unknown): string {
  const m = (err as { message?: string } | undefined)?.message;
  return m && m.trim() ? m : 'That did not go through. Please try again.';
}


export default function IntakeTab({ projectId, projectName }: IntakeTabProps) {
  const [data, setData] = useState<ProjectIntake | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'none' | 'error'>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState<number | null>(null);
  const [stageError, setStageError] = useState<Record<number, string>>({});
  // Per-row reasons the server gave for answers it did not take; the row keeps its draft.
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const got = await IntakeAPI.getIntake(projectId);
      if (got && Array.isArray(got.stages)) {
        setData(got);
        setState('ready');
      } else {
        setState('none');
      }
    } catch (err) {
      if ((err as { status?: number } | undefined)?.status === 404) {
        setState('none');
      } else {
        setLoadError(messageOf(err));
        setState('error');
      }
    }
  }, [projectId]);

  useEffect(() => {
    setState('loading');
    void load();
  }, [load]);

  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  const setDraft = (r: IntakeRequirement, d: Draft) => {
    setRowErrors((prev) => {
      if (!(r.id in prev)) return prev;
      const next = { ...prev };
      delete next[r.id];
      return next;
    });
    setDrafts((prev) => {
      const next = { ...prev };
      if (sameDraft(d, initialDraft(r))) delete next[r.id];
      else next[r.id] = d;
      return next;
    });
  };

  const saveStage = async (stage: number, reqs: IntakeRequirement[]) => {
    const answers = reqs
      .filter((r) => drafts[r.id])
      .map((r) => answerOf(r, drafts[r.id]))
      .filter((a): a is IntakeAnswer => a != null);
    if (answers.length === 0) return;
    setSaving(stage);
    setStageError((p) => ({ ...p, [stage]: '' }));
    try {
      const resp = await IntakeAPI.answerIntake(projectId, answers);
      // Only answers the server took clear their draft; a rejected one keeps the draft and
      // shows the server's reason on its row. An older server without `results` took them all.
      const results = Array.isArray(resp?.results) ? resp.results : null;
      const byId = new Map((results ?? []).map((x) => [x.id, x]));
      const rejected: Record<string, string> = {};
      for (const a of answers) {
        if (!results) continue;
        const got = byId.get(a.id);
        if (!got) rejected[a.id] = 'The server did not confirm this answer.';
        else if (got.status === 'rejected') rejected[a.id] = got.reason || 'Not accepted.';
      }
      setDrafts((prev) => {
        const next = { ...prev };
        for (const a of answers) if (!(a.id in rejected)) delete next[a.id];
        return next;
      });
      setRowErrors((prev) => {
        const next = { ...prev };
        for (const a of answers) delete next[a.id];
        return { ...next, ...rejected };
      });
      const n = Object.keys(rejected).length;
      if (n > 0) {
        setStageError((p) => ({
          ...p,
          [stage]: `${n} of ${answers.length} answer${answers.length === 1 ? ' was' : 's were'} not saved. The reason is shown on ${n === 1 ? 'that question' : 'each question'}.`,
        }));
      }
      await load();
    } catch (err) {
      setStageError((p) => ({ ...p, [stage]: messageOf(err) }));
    } finally {
      setSaving(null);
    }
  };

  const promptName = data?.project_name || projectName;
  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(chatPrompt(promptName));
      setCopied('copied');
    } catch {
      setCopied('failed');
    }
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied('idle'), 2500);
  };

  if (state === 'loading') {
    return (
      <div className="p-6 text-[13px] text-[var(--tx-lo)]" role="status" data-testid="intake-loading">
        Loading intake…
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="mx-auto w-full max-w-3xl p-4" data-testid="intake-error">
        <Banner tone="warn">
          Could not load the intake. {loadError}
          <div className="mt-2">
            <Button size="sm" onClick={() => { setState('loading'); void load(); }}>
              Retry
            </Button>
          </div>
        </Banner>
      </div>
    );
  }
  const stages = (data?.stages ?? []).filter((s) => (s.groups ?? []).some((g) => (g.requirements ?? []).length > 0));
  const j = data?.jurisdiction;

  return (
    <div className="mx-auto w-full min-w-0 max-w-3xl px-4 py-4" data-testid="intake">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[16px] font-[620] text-[var(--tx-hi)]">Intake</h2>
          {state === 'ready' && (
            <p className="text-[12.5px] text-[var(--tx-lo)]" data-testid="intake-open-count">
              {data!.open_count === 0 ? 'Nothing open' : `${data!.open_count} open`}
              {data!.loss_type && ` · ${data!.loss_type} loss`}
            </p>
          )}
        </div>
        <Button onClick={() => void copyPrompt()} aria-live="polite" data-testid="intake-continue-chat">
          {copied === 'copied' ? 'Copied' : 'Continue in chat'}
        </Button>
      </div>
      {copied === 'failed' && (
        <Banner tone="warn" className="mt-2">
          Could not copy. Paste this into the chat: <span className="select-all break-words">{chatPrompt(promptName)}</span>
        </Banner>
      )}

      {j && (j.state || j.note) && (
        <div className="mt-3 rounded-[var(--r-md)] border border-[var(--bd-subtle)] p-3" data-testid="intake-jurisdiction">
          <div className="text-[12.5px] font-[600] text-[var(--tx-hi)]">
            Jurisdiction: {j.state || 'state not known'}
            {j.matching_status && (
              <span className="ml-2 font-[450] text-[var(--tx-lo)]">matching: {j.matching_status.replace(/_/g, ' ')}</span>
            )}
          </div>
          {j.note && <p className="mt-1 break-words text-[12px] text-[var(--tx-mid)]">{j.note}</p>}
        </div>
      )}

      <ChatAnswersSection projectId={projectId} />

      {stages.length === 0 ? (
        <p className="mt-6 text-[13px] text-[var(--tx-lo)]" data-testid="intake-empty">
          Nothing to ask yet. Save the claim facts from the chat (or add notes and photos), then come back.
        </p>
      ) : (
        stages.map((s) => {
          const reqs = s.groups.flatMap((g) => g.requirements ?? []);
          const pending = reqs.filter((r) => drafts[r.id] && answerOf(r, drafts[r.id]) != null).length;
          const open = reqs.filter((r) => r.status === 'open').length;
          const hid = `intake-stage-${s.stage}`;
          return (
            <section
              key={s.stage}
              aria-labelledby={hid}
              className="mt-4 rounded-[var(--r-md)] border border-[var(--bd-subtle)] p-3"
              data-testid={hid}
            >
              <h3 id={hid} className="text-[14px] font-[600] text-[var(--tx-hi)]">
                {s.stage}. {s.title}
                <span className="ml-2 text-[11.5px] font-[450] text-[var(--tx-faint)]">
                  {open === 0 ? 'all filled' : `${open} open`}
                </span>
              </h3>
              {s.groups.filter((g) => (g.requirements ?? []).length > 0).map((g) => (
                <IntakeGroupBlock key={`${g.kind}:${g.group}`} g={g} drafts={drafts} errors={rowErrors} onDraft={setDraft} disabled={saving === s.stage} />
              ))}
              {stageError[s.stage] && (
                <Banner tone="warn" className="mt-2">
                  {stageError[s.stage]}
                </Banner>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={pending === 0 || saving != null}
                  onClick={() => void saveStage(s.stage, reqs)}
                  aria-label={`Save ${s.title} answers`}
                >
                  {saving === s.stage ? 'Saving…' : pending > 0 ? `Save ${pending} answer${pending === 1 ? '' : 's'}` : 'Save'}
                </Button>
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
